import { supabase } from '../lib/supabase';
import type { ChatMessageReaction } from '../components/Chat/reactions';
import { nomeSeguroParaStorage } from '../components/Chat/attachmentStorage';

// Avisar uma vez por sessão que as reações não estão instaladas neste banco.
let reacoesIndisponiveisAvisadas = false;

interface UserProfile {
  id: number;
  auth_id: string;
  name: string;
  avatar_url?: string;
  user_photo?: string;
}

interface UserMap {
  [key: string]: UserProfile;
}

export interface ChatRoom {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  type: 'project' | 'team' | 'direct' | 'general';
  project_id?: string;
  created_at?: string;
  updated_at?: string;
  project?: {
    id: string;
    name: string;
    type: string;
  };
}

// Envio de mensagem: a única variação hoje é a mensagem que carrega anexo, que
// pode ir sem texto. Fica como opção explícita, e não inferida, porque o
// serviço não vê o arquivo — ele sobe antes, em outra chamada.
export interface EnvioDeMensagemOpcoes {
  permitirTextoVazio?: boolean;
}

export interface ChatMessage {
  id: string;
  room_id: string;
  user_auth_id: string;
  content: string;
  organization_id?: string; // Adicionado para multi-tenancy
  is_system_message?: boolean;
  parent_id?: string;
  created_at?: string;
  updated_at?: string;
  // Nulos até a primeira edição / exclusão. `updated_at` não serve para nada
  // disso: nasce preenchido e é carimbado pelo próprio envio.
  edited_at?: string | null;
  deleted_at?: string | null;
  user?: {
    id: number;
    name: string;
    avatar_url?: string;
  };
  reply_to_message?: {
    id: string;
    content: string;
    user_auth_id: string;
    deleted_at?: string | null;
    user?: {
      id: number;
      name: string;
      avatar_url?: string;
    };
  };
  attachments?: ChatAttachment[];
  reactions?: ChatMessageReaction[];
  mentions?: ChatMention[];
}

export interface ChatAttachment {
  id: string;
  message_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  created_at?: string;
}

export interface ChatMention {
  message_id: string;
  user_auth_id: string;
  is_read: boolean;
  created_at?: string;
}

// Sem organization_id: a coluna não existe em chat_room_members (conferido em
// produção em 2026-09-28) e mandá-la num insert faz o PostgREST recusar a linha.
// O tenant do membro é o da sala.
export interface ChatRoomMember {
  room_id: string;
  user_auth_id: string;
  role: 'admin' | 'member' | 'guest';
  last_read_message_id?: string;
  joined_at?: string;
  user?: {
    id: number;
    name: string;
    avatar_url?: string;
  };
}

export interface PessoaDoChat {
  authId: string;
  nome: string;
  email: string | null;
  avatarUrl: string | null;
}

class ChatService {
  private validateOrganizationId(organizationId: string): void {
    if (!organizationId || typeof organizationId !== 'string') {
      throw new Error('Invalid organization ID');
    }
  }

  // Salas de chat
  async getChatRooms(organizationId: string): Promise<ChatRoom[]> {
    this.validateOrganizationId(organizationId);
    const { data, error } = await supabase
      .from('chat_rooms')
      .select('*')
      .eq('organization_id', organizationId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar salas de chat:', error);
      throw error;
    }

    return data || [];
  }

  async getChatRoomsByProject(projectId: string): Promise<ChatRoom[]> {
    const { data, error } = await supabase
      .from('chat_rooms')
      .select('*')
      .eq('project_id', projectId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar salas de chat do projeto:', error);
      throw error;
    }

    return data || [];
  }

  async createChatRoom(room: Omit<ChatRoom, 'id' | 'created_at' | 'updated_at'>): Promise<ChatRoom> {
    this.validateOrganizationId(room.organization_id);
    const { data, error } = await supabase
      .from('chat_rooms')
      .insert(room)
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar sala de chat:', error);
      throw error;
    }

    return data;
  }

  // Conversa privada entre o usuário autenticado e outra pessoa da
  // organização. Criar sala e membros só é possível pela RPC: as policies
  // barram o cliente de inserir sala direta ou membro nela, para que ninguém
  // se inclua numa conversa alheia.
  async getOrCreateDirectChat(organizationId: string, otherUserAuthId: string): Promise<string> {
    this.validateOrganizationId(organizationId);
    const { data, error } = await supabase.rpc('get_or_create_direct_chat', {
      p_organization_id: organizationId,
      p_other_user_auth_id: otherUserAuthId,
    });

    if (error) {
      console.error('Erro ao abrir conversa privada:', error);
      throw error;
    }
    if (!data) {
      throw new Error('Falha ao abrir conversa privada');
    }
    return data;
  }

  // Pessoas da organização com quem dá para abrir uma conversa privada.
  //
  // Mesma regra de `is_org_member` (e da RPC): a pessoa pode estar ligada à
  // organização por `organization_members` ou por `user_profile.organization_id`
  // — nem todo usuário tem linha nas duas. Sem join embutido do PostgREST, que
  // depende de FK entre as tabelas.
  async getPessoasDaOrganizacao(organizationId: string): Promise<PessoaDoChat[]> {
    this.validateOrganizationId(organizationId);

    const [perfisDiretos, vinculos] = await Promise.all([
      supabase
        .from('user_profile')
        .select('auth_id, name, email, user_photo')
        .eq('organization_id', organizationId),
      supabase
        .from('organization_members')
        .select('user_id')
        .eq('organization_id', organizationId),
    ]);

    if (perfisDiretos.error) {
      console.error('Erro ao buscar pessoas da organização:', perfisDiretos.error);
      throw perfisDiretos.error;
    }
    if (vinculos.error) {
      console.error('Erro ao buscar membros da organização:', vinculos.error);
    }

    type Perfil = { auth_id: string | null; name: string | null; email: string | null; user_photo: string | null };
    const perfis: Perfil[] = [...((perfisDiretos.data || []) as Perfil[])];

    const conhecidos = new Set(perfis.map(p => p.auth_id).filter(Boolean));
    const faltando = ((vinculos.data || []) as Array<{ user_id: string | null }>)
      .map(v => v.user_id)
      .filter((id): id is string => !!id && !conhecidos.has(id));

    if (faltando.length > 0) {
      const { data, error } = await supabase
        .from('user_profile')
        .select('auth_id, name, email, user_photo')
        .in('auth_id', faltando);
      if (error) {
        console.error('Erro ao buscar perfis dos membros da organização:', error);
      } else {
        perfis.push(...((data || []) as Perfil[]));
      }
    }

    const porAuthId = new Map<string, PessoaDoChat>();
    for (const perfil of perfis) {
      if (!perfil.auth_id || porAuthId.has(perfil.auth_id)) continue;
      porAuthId.set(perfil.auth_id, {
        authId: perfil.auth_id,
        nome: perfil.name || perfil.email || 'Usuário sem nome',
        email: perfil.email ?? null,
        avatarUrl: perfil.user_photo ?? null,
      });
    }

    return [...porAuthId.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }

  // Mensagens
  async getChatMessages(roomId: string, limit = 50, offset = 0): Promise<ChatMessage[]> {
    try {
      console.log('ChatService: Fetching messages', { roomId, limit, offset });
      
      // Paginar do mais recente pro mais antigo: `offset` 0 tem que ser a
      // conversa de agora, não o começo do histórico. Com a ordem ascendente,
      // abrir uma sala com mais de `limit` mensagens trazia as mais ANTIGAS, e a
      // subscription realtime, que pede a primeira página pra montar a mensagem
      // recebida, encontrava a mensagem inaugural da sala em vez da que chegou.
      // `id` desempata mensagens com o mesmo `created_at`, senão o Postgres
      // pode repetir ou pular linhas entre páginas.
      const { data: messagesDesc, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('room_id', roomId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(offset, offset + limit - 1);

      if (messagesError) {
        console.error('ChatService: Error fetching messages:', messagesError);
        throw messagesError;
      }

      if (!messagesDesc || messagesDesc.length === 0) {
        console.log('ChatService: No messages found');
        return [];
      }

      // A tela renderiza de cima pra baixo em ordem cronológica.
      const messages = [...messagesDesc].reverse();

      console.log('ChatService: Raw messages from DB:', messages);

      // Usuários, mensagens-pai (replies) e anexos não dependem uns dos
      // outros — só do resultado de `messages` acima — então buscar os três
      // em paralelo em vez de esperar cada query terminar pra começar a próxima.
      const userAuthIds = [...new Set(messages.map(m => m.user_auth_id))];
      const parentMessageIds = messages
        .filter(m => m.parent_id)
        .map(m => m.parent_id)
        .filter(Boolean);
      const messageIds = messages.map(m => m.id);

      console.log('ChatService: Parent message IDs found:', parentMessageIds);
      console.log('ChatService: Fetching attachments for message IDs:', messageIds);

      const [
        { data: users, error: usersError },
        { data: parentMessages, error: parentError },
        { data: attachments, error: attachmentsError },
        reactions
      ] = await Promise.all([
        supabase
          .from('user_profile')
          .select('id, auth_id, name, user_photo')
          .in('auth_id', userAuthIds),
        parentMessageIds.length > 0
          ? supabase
              .from('chat_messages')
              // `deleted_at` entra aqui para a citação de uma mensagem apagada
              // não continuar exibindo o texto que a pessoa mandou remover.
              .select('id, content, user_auth_id, deleted_at')
              .in('id', parentMessageIds)
          : Promise.resolve({ data: [] as any[], error: null }),
        supabase
          .from('chat_attachments')
          .select('*')
          .in('message_id', messageIds),
        this.getReactionsForMessages(messageIds)
      ]);

      if (usersError) {
        console.error('ChatService: Error fetching users:', usersError);
        return messages;
      }

      // Mapear usuários por auth_id
      const userMap = users?.reduce((acc, user) => {
        acc[user.auth_id] = {
          id: user.id,
          name: user.name,
          avatar_url: user.user_photo
        };
        return acc;
      }, {} as Record<string, { id: number; name: string; avatar_url?: string }>);

      let parentMessagesMap: Record<string, any> = {};

      if (parentError) {
        console.error('ChatService: Error fetching parent messages:', parentError);
      } else if (parentMessages) {
        console.log('ChatService: Found parent messages:', parentMessages);

        // Mapear mensagens pai por ID
        parentMessagesMap = parentMessages.reduce((acc, msg) => {
          acc[msg.id] = {
            id: msg.id,
            content: msg.content,
            user_auth_id: msg.user_auth_id,
            user: userMap?.[msg.user_auth_id]
          };
          return acc;
        }, {} as Record<string, any>);

        console.log('ChatService: Parent messages map:', parentMessagesMap);
      }

      if (attachmentsError) {
        console.error('ChatService: Error fetching attachments:', attachmentsError);
      }

      console.log('ChatService: Attachments found:', attachments);

      // Mapear reações por message_id
      const reactionsMap: Record<string, ChatMessageReaction[]> = {};
      for (const reaction of reactions) {
        (reactionsMap[reaction.message_id] ||= []).push(reaction);
      }

      // Mapear anexos por message_id
      const attachmentsMap: Record<string, ChatAttachment[]> = {};
      if (attachments) {
        attachments.forEach(attachment => {
          if (!attachmentsMap[attachment.message_id]) {
            attachmentsMap[attachment.message_id] = [];
          }
          attachmentsMap[attachment.message_id].push(attachment);
        });
      }

      console.log('ChatService: Attachments map:', attachmentsMap);

      // Adicionar informações dos usuários, mensagens de reply e anexos às mensagens
      const messagesWithUsers = messages.map(message => {
        const replyToMessage = message.parent_id ? parentMessagesMap[message.parent_id] : undefined;
        const messageAttachments = attachmentsMap[message.id] || [];
        
        console.log('ChatService: Processing message:', {
          messageId: message.id,
          content: message.content,
          parent_id: message.parent_id,
          replyToMessage,
          hasParentId: !!message.parent_id,
          parentInMap: message.parent_id ? !!parentMessagesMap[message.parent_id] : false,
          attachments: messageAttachments
        });
        
        return {
          ...message,
          user: userMap?.[message.user_auth_id],
          reply_to_message: replyToMessage,
          attachments: messageAttachments,
          reactions: reactionsMap[message.id] || []
        };
      });

      console.log('ChatService: Messages with users, replies and attachments:', messagesWithUsers);
      return messagesWithUsers;
    } catch (err) {
      console.error('ChatService: Error in getChatMessages:', err);
      throw err;
    }
  }

  async sendChatMessage(
    message: Omit<ChatMessage, 'id' | 'created_at' | 'updated_at'>,
    opcoes?: EnvioDeMensagemOpcoes
  ): Promise<ChatMessage> {
    console.log('ChatService: Sending message to Supabase', message);

    // Validar dados obrigatórios.
    //
    // Texto vazio só é aceito quando quem envia declara que a mensagem carrega
    // um anexo: a mensagem de voz nasce sem texto de propósito (o nome do
    // arquivo é o instante da gravação, que não diz nada a quem lê — ver
    // attachmentMessage.ts). Sem essa exceção, toda mensagem de voz morria
    // aqui, depois de o áudio já ter subido para o Storage. Numa mensagem de
    // texto comum, vazio continua sendo dado faltando.
    const textoVazioPermitido = opcoes?.permitirTextoVazio === true;
    if (
      !message.room_id ||
      !message.user_auth_id ||
      (!message.content && !textoVazioPermitido)
    ) {
      console.error('ChatService: Missing required message data', message);
      throw new Error('Dados da mensagem incompletos');
    }

    try {
      // Se não tiver organization_id, buscar da sala
      let organizationId = message.organization_id;
      if (!organizationId) {
        const { data: roomData, error: roomError } = await supabase
          .from('chat_rooms')
          .select('organization_id')
          .eq('id', message.room_id)
          .single();

        if (roomError) {
          console.error('ChatService: Error fetching room organization_id:', roomError);
          throw roomError;
        }

        organizationId = roomData.organization_id;
      }

      // Inserir a mensagem com organization_id
      const messageWithOrg = { ...message, organization_id: organizationId };
      const { data: messageData, error: messageError } = await supabase
        .from('chat_messages')
        .insert(messageWithOrg)
        .select('*')
        .single();

      if (messageError) {
        console.error('ChatService: Error inserting message:', messageError);
        throw messageError;
      }

      if (!messageData) {
        console.error('ChatService: No message data returned');
        throw new Error('Falha ao enviar mensagem');
      }

      // Buscar informações do usuário
      const { data: userData, error: userError } = await supabase
        .from('user_profile')
        .select('id, name, user_photo')
        .eq('auth_id', message.user_auth_id)
        .single();

      if (userError) {
        console.error('ChatService: Error fetching user data:', userError);
      }

      // Se a mensagem é um reply, buscar a mensagem pai
      let replyToMessage = undefined;
      if (messageData.parent_id) {
        console.log('ChatService: Fetching parent message for reply:', messageData.parent_id);
        
        const { data: parentMessage, error: parentError } = await supabase
          .from('chat_messages')
          .select('id, content, user_auth_id')
          .eq('id', messageData.parent_id)
          .single();

        if (parentError) {
          console.error('ChatService: Error fetching parent message:', parentError);
        } else if (parentMessage) {
          // Buscar informações do usuário da mensagem pai
          const { data: parentUserData, error: parentUserError } = await supabase
            .from('user_profile')
            .select('id, name, user_photo')
            .eq('auth_id', parentMessage.user_auth_id)
            .single();

          if (parentUserError) {
            console.error('ChatService: Error fetching parent user data:', parentUserError);
          }

          replyToMessage = {
            id: parentMessage.id,
            content: parentMessage.content,
            user_auth_id: parentMessage.user_auth_id,
            user: parentUserData ? {
              id: parentUserData.id,
              name: parentUserData.name,
              avatar_url: parentUserData.user_photo
            } : undefined
          };
          
          console.log('ChatService: Parent message found for reply:', replyToMessage);
        }
      }

      // Combinar os dados
      const fullMessage: ChatMessage = {
        ...messageData,
        user: userData ? {
          id: userData.id,
          name: userData.name,
          avatar_url: userData.user_photo
        } : undefined,
        reply_to_message: replyToMessage
      };

      // Atualizar a data de atualização da sala
      await supabase
        .from('chat_rooms')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', message.room_id);

      console.log('ChatService: Message sent successfully with reply data:', fullMessage);
      return fullMessage;
    } catch (err) {
      console.error('ChatService: Error in sendChatMessage:', err);
      throw err;
    }
  }

  // Membros da sala
  // Reações das mensagens. Carregadas em lote junto com a leva de mensagens:
  // uma consulta por mensagem renderizada seria uma enxurrada de requisições.
  async getReactionsForMessages(messageIds: string[]): Promise<ChatMessageReaction[]> {
    if (messageIds.length === 0) return [];

    const { data, error } = await supabase
      .from('chat_message_reactions')
      .select('id, message_id, user_auth_id, emoji, created_at')
      .in('message_id', messageIds);

    if (error) {
      // 42P01: a migration de reações não foi aplicada neste banco. Sem a
      // tabela ninguém reagiu — o chat segue funcionando sem o recurso, em vez
      // de a sala inteira falhar ao carregar.
      if (error.code === '42P01') {
        if (!reacoesIndisponiveisAvisadas) {
          reacoesIndisponiveisAvisadas = true;
          console.warn(
            'Reações de chat desativadas: a tabela chat_message_reactions não existe neste banco ' +
            '(aplique supabase/migrations/20260909120000_chat_message_reactions.sql para habilitar).'
          );
        }
        return [];
      }

      console.error('ChatService: Erro ao buscar reações:', error);
      return [];
    }

    return data || [];
  }

  // Grava a reação da pessoa: troca a que existia, ou remove se for a mesma.
  // O upsert usa a UNIQUE (message_id, user_auth_id), que é o que faz valer
  // "uma reação por pessoa" mesmo com duas abas abertas.
  async setReaction(
    messageId: string,
    userAuthId: string,
    emoji: string,
    emojiAtual: string | null
  ): Promise<void> {
    if (emojiAtual === emoji) {
      const { error } = await supabase
        .from('chat_message_reactions')
        .delete()
        .match({ message_id: messageId, user_auth_id: userAuthId });

      if (error) {
        console.error('ChatService: Erro ao remover reação:', error);
        throw error;
      }
      return;
    }

    const { error } = await supabase
      .from('chat_message_reactions')
      .upsert(
        { message_id: messageId, user_auth_id: userAuthId, emoji },
        { onConflict: 'message_id,user_auth_id' }
      );

    if (error) {
      console.error('ChatService: Erro ao salvar reação:', error);
      throw error;
    }
  }

  // Salas em que o usuário realmente participa. `getChatRooms` devolve todas as
  // salas da organização, então assinar a lista inteira abria um canal Realtime
  // por projeto da empresa e gastava a cota de eventos com conversas alheias.
  async getRoomIdsForUser(userAuthId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_auth_id', userAuthId);

    if (error) {
      console.error('Erro ao buscar salas do usuário:', error);
      throw error;
    }

    return (data || []).map(row => row.room_id);
  }

  async isRoomMember(roomId: string, userAuthId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('room_id', roomId)
      .eq('user_auth_id', userAuthId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao verificar participação na sala:', error);
      throw error;
    }
    return !!data;
  }

  async getChatRoomMembers(roomId: string): Promise<ChatRoomMember[]> {
    console.log('ChatService: Buscando membros da sala:', roomId);
    
    // Primeiro, buscar os membros da sala
    const { data: members, error } = await supabase
      .from('chat_room_members')
      .select('*')
      .eq('room_id', roomId);

    if (error) {
      console.error('Erro ao buscar membros da sala:', error);
      throw error;
    }

    console.log('ChatService: Membros encontrados:', members);

    if (!members || members.length === 0) {
      return [];
    }

    // Buscar informações dos usuários
    const userAuthIds = members.map(m => m.user_auth_id);
    console.log('ChatService: Buscando perfis para auth_ids:', userAuthIds);
    
    const { data: users, error: usersError } = await supabase
      .from('user_profile')
      .select('id, auth_id, name, user_photo')
      .in('auth_id', userAuthIds);
      
    if (usersError) {
      console.error('Erro ao buscar informações dos usuários:', usersError);
    }
    
    console.log('ChatService: Perfis de usuário encontrados:', users);

    // Mapear auth_id para informações do usuário
    const userMap: { [key: string]: any } = {};
    if (users) {
      users.forEach(user => {
        userMap[user.auth_id] = user;
      });
    }
    
    console.log('ChatService: Mapa de usuários:', userMap);

    // Adicionar informações do usuário a cada membro
    const formattedMembers = members.map(member => {
      const user = userMap[member.user_auth_id];
      
      console.log(`ChatService: Processando membro ${member.user_auth_id}:`, {
        member,
        user,
        hasUser: !!user
      });
      
      return {
        ...member,
        user: user ? {
          id: user.id,
          name: user.name || 'Usuário sem nome',
          avatar_url: user.user_photo || undefined
        } : {
          id: 0,
          name: `Usuário ${member.user_auth_id.substring(0, 8)}`,
          avatar_url: undefined
        }
      };
    });

    console.log('ChatService: Membros formatados finais:', formattedMembers);
    return formattedMembers;
  }

  async addChatRoomMember(member: Omit<ChatRoomMember, 'joined_at'>): Promise<ChatRoomMember> {
    const { data, error } = await supabase
      .from('chat_room_members')
      .insert(member)
      .select()
      .single();

    if (error) {
      console.error('Erro ao adicionar membro à sala:', error);
      throw error;
    }

    return data;
  }

  async removeChatRoomMember(roomId: string, userAuthId: string): Promise<void> {
    console.log('ChatService: Removendo membro da sala', { roomId, userAuthId });
    
    const { error } = await supabase
      .from('chat_room_members')
      .delete()
      .match({ room_id: roomId, user_auth_id: userAuthId });

    if (error) {
      console.error('Erro ao remover membro da sala:', error);
      throw error;
    }

    console.log('ChatService: Membro removido com sucesso');
  }

  async isRoomAdmin(roomId: string, userAuthId: string): Promise<boolean> {
    console.log('ChatService: Verificando se usuário é admin da sala', { roomId, userAuthId });
    
    const { data, error } = await supabase
      .from('chat_room_members')
      .select('role')
      .match({ room_id: roomId, user_auth_id: userAuthId })
      .single();

    if (error) {
      console.error('Erro ao verificar role do usuário:', error);
      return false;
    }

    const isAdmin = data?.role === 'admin';
    console.log('ChatService: Usuário é admin?', isAdmin);
    return isAdmin;
  }

  async updateChatRoomMemberRole(roomId: string, userAuthId: string, newRole: 'admin' | 'member' | 'guest'): Promise<void> {
    console.log('ChatService: Atualizando papel do membro', { roomId, userAuthId, newRole });
    
    const { error } = await supabase
      .from('chat_room_members')
      .update({ role: newRole })
      .match({ room_id: roomId, user_auth_id: userAuthId });

    if (error) {
      console.error('Erro ao atualizar papel do membro:', error);
      throw error;
    }

    console.log('ChatService: Papel do membro atualizado com sucesso');
  }

  // Anexos
  //
  // Subir o arquivo é a etapa lenta (segundos, num vídeo de dezenas de MB) e
  // fica separada do registro de propósito: quem envia sobe o arquivo ANTES de
  // criar a mensagem, para que a linha de `chat_attachments` entre logo depois
  // do INSERT da mensagem. O Realtime avisa os outros clientes no INSERT da
  // mensagem, e eles vão buscar os anexos na hora — se o upload ainda estivesse
  // correndo aqui, a mensagem chegaria sem o anexo e ficaria assim.
  async uploadAttachmentFile(file: File, organizationId: string): Promise<string> {
    // O caminho não depende do id da mensagem, que nem existe ainda. Um uuid
    // por upload mantém os arquivos isolados entre si e evita colisão de nomes
    // iguais enviados por pessoas diferentes.
    //
    // A organização abre o caminho porque a policy de INSERT do bucket lê o
    // primeiro segmento para decidir se o upload pertence a quem está
    // enviando — o registro em chat_attachments, que é a fonte de verdade da
    // leitura, só existe depois que a mensagem é criada.
    if (!organizationId) {
      throw new Error('organizationId é obrigatório para anexar arquivo');
    }

    const filePath = `${organizationId}/${crypto.randomUUID()}/${nomeSeguroParaStorage(file.name)}`;

    const { error: uploadError } = await supabase.storage
      .from('chat-attachments')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Erro ao fazer upload do arquivo:', uploadError);
      throw uploadError;
    }

    return filePath;
  }

  async registerChatAttachment(
    messageId: string,
    file: File,
    filePath: string
  ): Promise<ChatAttachment> {
    const { data, error } = await supabase
      .from('chat_attachments')
      .insert({
        message_id: messageId,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        file_path: filePath
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao registrar anexo:', error);
      throw error;
    }

    return data;
  }

  // Editar e excluir a própria mensagem
  //
  // Quem pode é decidido pelo RLS (policies de autor em chat_messages), não
  // aqui: checagem que só existe no cliente não é checagem. A tela esconde as
  // ações das mensagens dos outros para não oferecer o que o banco vai negar.

  async editChatMessage(messageId: string, novoConteudo: string): Promise<ChatMessage> {
    const conteudo = novoConteudo.trim();
    if (!conteudo) {
      throw new Error('A mensagem editada não pode ficar vazia');
    }

    const agora = new Date().toISOString();

    const { data, error } = await supabase
      .from('chat_messages')
      .update({ content: conteudo, edited_at: agora, updated_at: agora })
      .eq('id', messageId)
      .select('*')
      .single();

    if (error) {
      console.error('ChatService: Erro ao editar mensagem:', error);
      throw error;
    }

    // Sem linha de volta com `error` nulo significa que o RLS filtrou a
    // mensagem: não é minha, ou já está apagada. Falhar aqui evita a tela
    // exibir como salva uma edição que o banco não aceitou.
    if (!data) {
      throw new Error('Não foi possível editar esta mensagem');
    }

    return data;
  }

  // Exclusão lógica: a linha fica, o conteúdo sai. A linha precisa ficar
  // porque a FK de resposta é ON DELETE SET NULL — apagar de verdade deixaria
  // as respostas órfãs, penduradas em lugar nenhum da conversa.
  async deleteChatMessage(messageId: string): Promise<ChatMessage> {
    // Os anexos saem antes: enquanto o arquivo estiver no bucket e a linha de
    // `chat_attachments` existir, o caminho continua rendendo URL assinada, e
    // "excluí a mensagem" viraria mentira para quem já tinha a sala aberta.
    await this.removeAttachmentsOfMessage(messageId);

    const agora = new Date().toISOString();

    const { data, error } = await supabase
      .from('chat_messages')
      .update({ content: '', deleted_at: agora, updated_at: agora })
      .eq('id', messageId)
      .select('*')
      .single();

    if (error) {
      console.error('ChatService: Erro ao excluir mensagem:', error);
      throw error;
    }

    if (!data) {
      throw new Error('Não foi possível excluir esta mensagem');
    }

    return data;
  }

  // Tira do Storage e da tabela os anexos de uma mensagem. Falha de Storage
  // não derruba a exclusão: um arquivo esquecido no bucket é menos grave que a
  // mensagem continuar na sala depois de a pessoa mandar apagar.
  private async removeAttachmentsOfMessage(messageId: string): Promise<void> {
    const { data: anexos, error } = await supabase
      .from('chat_attachments')
      .select('id, file_path')
      .eq('message_id', messageId);

    if (error) {
      console.error('ChatService: Erro ao listar anexos da mensagem:', error);
      return;
    }

    if (!anexos || anexos.length === 0) return;

    // A ordem importa: a policy de SELECT do bucket confere o anexo em
    // `chat_attachments`. Apagando a linha primeiro, o arquivo fica inalcançável
    // e nunca sai do Storage.
    const { error: storageError } = await supabase.storage
      .from('chat-attachments')
      .remove(anexos.map(a => a.file_path));

    if (storageError) {
      console.error('ChatService: Erro ao remover arquivos do Storage:', storageError);
    }

    const { error: rowsError } = await supabase
      .from('chat_attachments')
      .delete()
      .eq('message_id', messageId);

    if (rowsError) {
      console.error('ChatService: Erro ao remover anexos da mensagem:', rowsError);
    }
  }

  // Menções
  async createChatMention(mention: Omit<ChatMention, 'created_at'>): Promise<ChatMention> {
    const { data, error } = await supabase
      .from('chat_mentions')
      .insert(mention)
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar menção:', error);
      throw error;
    }

    return data;
  }

  async markMentionAsRead(messageId: string, userAuthId: string): Promise<void> {
    const { error } = await supabase
      .from('chat_mentions')
      .update({ is_read: true })
      .match({ message_id: messageId, user_auth_id: userAuthId });

    if (error) {
      console.error('Erro ao marcar menção como lida:', error);
      throw error;
    }
  }

  // Marcar mensagens como lidas
  async markMessagesAsRead(roomId: string, userAuthId: string, lastMessageId: string): Promise<void> {
    const { error } = await supabase
      .from('chat_room_members')
      .update({ last_read_message_id: lastMessageId })
      .match({ room_id: roomId, user_auth_id: userAuthId });

    if (error) {
      console.error('Erro ao marcar mensagens como lidas:', error);
      throw error;
    }
  }

  // Não lidas por sala, direto do banco (ver migration
  // 20260921000001_chat_unread_counts.sql). Até então o contador do botão
  // flutuante só existia em memória e sumia a cada recarga, apesar de
  // `last_read_message_id` já ser gravado por markMessagesAsRead.
  //
  // Nunca lança: badge é enfeite, não pode derrubar o carregamento do chat — e
  // enquanto a migration não estiver aplicada no ambiente a RPC nem existe.
  async getUnreadCounts(): Promise<Record<string, number>> {
    const { data, error } = await supabase.rpc('get_chat_unread_counts');

    if (error) {
      console.error('ChatService: Erro ao buscar não lidas do chat:', error);
      return {};
    }

    const porSala: Record<string, number> = {};
    for (const linha of (data || []) as { room_id: string; unread_count: number | string }[]) {
      // `bigint` chega como string pelo PostgREST.
      const total = Number(linha.unread_count);
      if (linha.room_id && Number.isFinite(total) && total > 0) {
        porSala[linha.room_id] = total;
      }
    }
    return porSala;
  }

  // Nome de exibição de quem está logado, para reconhecer `@Nome Completo` nas
  // mensagens que chegam (ver components/Chat/mentionMatch.ts).
  async getDisplayName(userAuthId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('user_profile')
      .select('name')
      .eq('auth_id', userAuthId)
      .maybeSingle<{ name: string | null }>();

    if (error) {
      console.error('ChatService: Erro ao buscar nome do usuário:', error);
      return null;
    }
    return data?.name ?? null;
  }

  async getChatRoom(roomId: string): Promise<ChatRoom | null> {
    try {
      console.log('ChatService: Fetching chat room:', roomId);
      
      // First, get the chat room data
      const { data: roomData, error: roomError } = await supabase
        .from('chat_rooms')
        .select('*')
        .eq('id', roomId)
        .single();

      if (roomError) {
        console.error('ChatService: Error fetching chat room:', roomError);
        throw roomError;
      }

      if (!roomData) {
        console.log('ChatService: No room found with ID:', roomId);
        return null;
      }

      // If the room has a project_id, fetch the project data
      if (roomData.project_id) {
        console.log('ChatService: Fetching project data for project_id:', roomData.project_id);
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('id, name, type')
          .eq('id', roomData.project_id)
          .single();

        if (projectError) {
          console.error('ChatService: Error fetching project data:', projectError);
          // Don't throw here, just log the error and continue with room data
        } else if (projectData) {
          console.log('ChatService: Project data found:', projectData);
          return {
            ...roomData,
            project: projectData
          };
        }
      }

      // Return room data even if project data is missing or errored
      console.log('ChatService: Returning room data without project:', roomData);
      return roomData;
    } catch (err) {
      console.error('ChatService: Error in getChatRoom:', err);
      return null;
    }
  }
}

export const chatService = new ChatService(); 